import { Controller } from '@hotwired/stimulus';

// How far below the top of the viewport a section's top edge has to travel
// before it takes over. Keeping this close to the top stops the next section
// lighting up while the current one still fills the screen, and it clears the
// scroll-mt-8 applied when jumping straight to a section.
const ACTIVATION_OFFSET = 64;

// Highlights the side-nav item for the FAQ section currently in view.
// The active section is the last one whose top edge has scrolled past a line
// near the top of the viewport, so no section above the current one stays lit.
//
// Place data-controller="faq-nav" on a wrapper containing:
//   data-faq-nav-target="section"   - each FAQ section card (must have an id)
//   data-faq-nav-target="link"      - each nav link, in the same order
//   data-faq-nav-target="indicator" - the rail highlight moved to the active link
// plus data-faq-nav-active-class and data-faq-nav-inactive-class.
export default class extends Controller {
  static targets = ['section', 'link', 'indicator'];
  static classes = ['active', 'inactive'];

  connect() {
    this.activeIndex = -1;
    this.frame = null;
    this.schedule = this.schedule.bind(this);

    window.addEventListener('scroll', this.schedule, { passive: true });
    window.addEventListener('resize', this.schedule);
    // <details> toggles change section offsets and the toggle event does not bubble
    this.element.addEventListener('toggle', this.schedule, true);

    this.update();
  }

  disconnect() {
    window.removeEventListener('scroll', this.schedule);
    window.removeEventListener('resize', this.schedule);
    this.element.removeEventListener('toggle', this.schedule, true);

    if (this.frame) {
      cancelAnimationFrame(this.frame);
      this.frame = null;
    }
  }

  scrollToSection(event) {
    const section = document.getElementById(event.params.sectionId);
    if (!section) return;

    event.preventDefault();
    section.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  schedule() {
    if (this.frame) return;

    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      this.update();
    });
  }

  update() {
    if (!this.hasSectionTarget || !this.hasLinkTarget) return;

    const index = this.currentIndex();

    if (index !== this.activeIndex) {
      this.activeIndex = index;
      this.linkTargets.forEach((link, i) => this.setLinkState(link, i === index));
    }

    this.moveIndicator(index);
  }

  currentIndex() {
    const scrolledToBottom =
      window.innerHeight + window.scrollY >=
      document.documentElement.scrollHeight - 2;

    // The final section can be too short to ever cross the threshold
    if (scrolledToBottom) return this.sectionTargets.length - 1;

    let index = 0;

    this.sectionTargets.forEach((section, i) => {
      if (section.getBoundingClientRect().top <= ACTIVATION_OFFSET) index = i;
    });

    return index;
  }

  setLinkState(link, active) {
    if (this.hasActiveClass) {
      this.activeClasses.forEach((name) => link.classList.toggle(name, active));
    }

    if (this.hasInactiveClass) {
      this.inactiveClasses.forEach((name) =>
        link.classList.toggle(name, !active)
      );
    }

    if (active) {
      link.setAttribute('aria-current', 'true');
    } else {
      link.removeAttribute('aria-current');
    }
  }

  moveIndicator(index) {
    if (!this.hasIndicatorTarget) return;

    const link = this.linkTargets[index];
    // Hidden on mobile, so there is nothing to measure or move
    if (!link || link.offsetParent === null) return;

    this.indicatorTarget.style.top = `${link.offsetTop}px`;
    this.indicatorTarget.style.height = `${link.offsetHeight}px`;
    this.indicatorTarget.style.opacity = '1';
  }
}
